import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { usePopup } from '../context/PopupContext';
import { serviceCategories } from '../data/services';
import TopBar from './TopBar';

export default function Header({ hideRegisterButton = false }) {
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [expandedMobileCat, setExpandedMobileCat] = useState(null);
  const { openPopup } = usePopup();
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMegaMenuOpen(false);
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setExpandedMobileCat(null);
  }, [location]);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setMegaMenuOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setMegaMenuOpen(false), 200);
  };

  const navLinkClass = "text-white/80 hover:text-[#D4AF37] transition-colors text-sm font-medium tracking-wide";

  return (
    <>
      <TopBar />
      <header
        data-testid="main-header"
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled ? 'top-0 bg-[#0A1128]/95 shadow-lg shadow-black/20' : 'top-[40px] bg-[#0A1128]/70'
        } backdrop-blur-xl border-b border-white/5`}
      >
      <div className="container mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src="/assets/img/brand/logo-light.png" alt="Trademark Legal Forum" className="h-8 sm:h-12 w-auto" /> 
         
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          <Link to="/" data-testid="nav-home" className={navLinkClass}>Home</Link>
          <Link to="/about" data-testid="nav-about" className={navLinkClass}>About Us</Link>
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              data-testid="nav-services-trigger"
              className={`flex items-center gap-1 ${navLinkClass}`}
            >
              Services
              <ChevronDown size={14} className={`transition-transform duration-200 ${megaMenuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <Link to="/contact" data-testid="nav-contact" className={navLinkClass}>Contact Us</Link>

          {/* Mega Menu Dropdown */}
          {megaMenuOpen && (
            <div
              data-testid="mega-menu"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="absolute top-full left-0 w-full animate-slide-down"
              style={{ zIndex: 100 }}
            >
              <div className="bg-[#0B1530] border-t border-[#1D2D50] shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                <div className="container mx-auto px-6 md:px-12 py-6">
                  <div className="grid grid-cols-6 gap-6">
                    {serviceCategories.map(cat => (
                      <div key={cat.id} className="min-w-0">
                        <h4 className="text-[#D4AF37] font-bold text-[10px] uppercase tracking-[0.15em] mb-3 font-['Outfit'] pb-2 border-b border-[#D4AF37]/20 whitespace-nowrap overflow-hidden text-ellipsis">
                          {cat.title}
                        </h4>
                        <ScrollArea className={cat.services.length > 8 ? 'h-[240px]' : ''}>
                          <ul className="space-y-0.5 pr-2">
                            {cat.services.map(svc => (
                              <li key={svc.slug}>
                                <Link
                                  to={`/services/${svc.slug}`}
                                  data-testid={`mega-menu-${svc.slug}`}
                                  className="text-white/60 hover:text-[#D4AF37] text-[13px] leading-tight transition-colors block py-1.5 hover:translate-x-0.5 duration-200"
                                >
                                  {svc.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </ScrollArea>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
              </div>
            </div>
          )}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3 z-10">
          <button
            data-testid="header-register-btn"
            onClick={() => openPopup()}
            className="bg-[#D4AF37] text-[#0A1128] font-bold py-2.5 px-6 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wide"
          >
            Register My Trademark
          </button>
          <button
            data-testid="header-chat-btn"
            className="js-chat border border-[#D4AF37]/50 text-[#D4AF37] font-bold py-2.5 px-6 rounded-sm text-sm transition-all duration-300 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] font-['Outfit'] tracking-wide"
          >
            Chat Now
          </button>
        </div>

        {/* Mobile Menu Trigger */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-trigger" className="lg:hidden text-white z-10">
              <Menu size={24} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#0A1128] border-l border-[#1D2D50] w-[300px] p-0">
            <div className="flex flex-col h-full">
              <div className="p-6 border-b border-[#1D2D50]">
                <span className="text-xl font-bold font-['Outfit'] text-white">
                  my <span className="text-[#D4AF37]">tm</span> brand
                </span>
              </div>
              <ScrollArea className="flex-1">
                <nav className="p-4 space-y-1">
                  <Link to="/" data-testid="mobile-nav-home" className="block px-4 py-3 text-white/80 hover:text-[#D4AF37] hover:bg-[#D4AF37]/5 rounded-sm transition-colors text-sm">
                    Home
                  </Link>
                  <Link to="/about" data-testid="mobile-nav-about" className="block px-4 py-3 text-white/80 hover:text-[#D4AF37] hover:bg-[#D4AF37]/5 rounded-sm transition-colors text-sm">
                    About Us
                  </Link>

                  {/* Services Accordion */}
                  <div>
                    <button
                      data-testid="mobile-nav-services"
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className="w-full flex items-center justify-between px-4 py-3 text-white/80 hover:text-[#D4AF37] hover:bg-[#D4AF37]/5 rounded-sm transition-colors text-sm"
                    >
                      Services
                      <ChevronDown size={14} className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileServicesOpen && (
                      <div className="ml-2 mt-1 space-y-1">
                        {serviceCategories.map(cat => (
                          <div key={cat.id}>
                            <button
                              onClick={() => setExpandedMobileCat(expandedMobileCat === cat.id ? null : cat.id)}
                              className="w-full flex items-center justify-between px-4 py-2 text-[#D4AF37]/80 text-xs uppercase tracking-wider font-bold hover:text-[#D4AF37] transition-colors"
                            >
                              {cat.title}
                              <ChevronRight size={12} className={`transition-transform ${expandedMobileCat === cat.id ? 'rotate-90' : ''}`} />
                            </button>
                            {expandedMobileCat === cat.id && (
                              <div className="ml-2 space-y-0.5">
                                {cat.services.map(svc => (
                                  <Link
                                    key={svc.slug}
                                    to={`/services/${svc.slug}`}
                                    className="block px-4 py-2 text-white/60 hover:text-[#D4AF37] text-xs transition-colors"
                                  >
                                    {svc.title}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Link to="/contact" data-testid="mobile-nav-contact" className="block px-4 py-3 text-white/80 hover:text-[#D4AF37] hover:bg-[#D4AF37]/5 rounded-sm transition-colors text-sm">
                    Contact Us
                  </Link>
                </nav>
              </ScrollArea>
              <div className="p-4 border-t border-[#1D2D50] space-y-2">
                {!hideRegisterButton && (
                  <button
                    data-testid="mobile-register-btn"
                    onClick={() => { setMobileOpen(false); openPopup(); }}
                    className="w-full bg-[#D4AF37] text-[#0A1128] font-bold py-3 rounded-sm text-sm font-['Outfit'] tracking-wide"
                  >
                    Register My Trademark
                  </button>
                )}
                <button
                  data-testid="mobile-chat-btn"
                  className="js-chat w-full border border-[#D4AF37]/50 text-[#D4AF37] font-bold py-3 rounded-sm text-sm font-['Outfit'] tracking-wide"
                >
                  Chat Now
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      </header>
    </>
  );
}
